import React, { useEffect, useId, useRef, useState } from 'react';
import { Plus, Minus, AlertTriangle } from 'lucide-react';
import Modal from '../common/Modal';
import { MEDIA, matchesMedia } from '../../utils/breakpoints';
import { parseQuantity, sanitizeQuantityText, quantityNeedsConfirm } from './stockTakeLogic';
import { useScanBurstGuard } from './scanBurst';

// "How many?" after scanning an IC document line. Scanning the same barcode again
// while it is open adds 1 (handled by StockTake); the quantity field is marked
// data-scan-capture so a scan that lands in it counts as a scan, not as digits.
const QuantityPopup = ({ show, popupItem, quantityText, setQuantityText, onConfirm, onCancel }) => {
  const inputId = useId();
  const inputRef = useRef(null);
  const addRef = useRef(null);
  // Big quantities need a second tap; reset whenever the item or quantity changes
  const [confirmingLarge, setConfirmingLarge] = useState(false);
  useEffect(() => setConfirmingLarge(false), [popupItem, quantityText]);

  const guardScan = useScanBurstGuard(setQuantityText);

  if (!show || !popupItem) return null;

  const quantity = parseQuantity(quantityText);
  const valid = quantity !== null && quantity >= 1;
  const current = popupItem.currentScanned;
  const expected = popupItem.quantity;
  const newTotal = current + (valid ? quantity : 0);
  const remaining = expected - current;
  const needsConfirm = valid && quantityNeedsConfirm(newTotal, expected);
  const name = popupItem.description || popupItem.partName || 'Unknown item';

  const step = (delta) => setQuantityText(String(Math.max(1, (quantity || 0) + delta)));

  const confirm = () => {
    if (!valid) {
      inputRef.current?.focus();
      return;
    }
    if (needsConfirm && !confirmingLarge) {
      setConfirmingLarge(true);
      return;
    }
    onConfirm(quantity);
  };

  const handleKeyDown = (e) => {
    if (guardScan(e)) return;
    if (e.key === 'Enter') {
      e.preventDefault();
      confirm();
    }
  };

  // On touch devices focusing the field would pop the on-screen keyboard over the
  // dialog after every scan, so the Add button takes focus there instead.
  const initialFocusRef = matchesMedia(MEDIA.coarsePointer) ? addRef : inputRef;

  return (
    <Modal
      open
      onClose={onCancel}
      title="Add quantity"
      size="sm"
      tone={confirmingLarge ? 'warning' : 'default'}
      initialFocusRef={initialFocusRef}
      className="st-qty-modal"
      footer={
        <>
          <button type="button" className="st-btn st-btn-secondary" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            ref={addRef}
            className={`st-btn ${confirmingLarge ? 'st-btn-warning' : 'st-btn-primary'}`}
            onClick={confirm}
            disabled={!valid}
          >
            {confirmingLarge ? `Yes, add ${quantity}` : `Add ${valid ? quantity : ''}`.trim()}
          </button>
        </>
      }
    >
      <div className="st-qty-item">
        <p className="st-qty-name">{name}</p>
        <p className="st-qty-barcode">{popupItem.barcode}</p>
      </div>

      <dl className="st-qty-stats">
        <div>
          <dt>Expected</dt>
          <dd>{expected}</dd>
        </div>
        <div>
          <dt>Already counted</dt>
          <dd>{current}</dd>
        </div>
      </dl>

      <label className="st-qty-label" htmlFor={inputId}>Quantity to add</label>
      <div className="st-qty-controls">
        <button
          type="button"
          className="st-qty-step"
          onClick={() => step(-1)}
          disabled={!valid || quantity <= 1}
          aria-label="Decrease quantity by 1"
        >
          <Minus size={24} aria-hidden="true" />
        </button>
        <input
          id={inputId}
          ref={inputRef}
          type="text"
          inputMode="numeric"
          pattern="[0-9]*"
          autoComplete="off"
          enterKeyHint="done"
          data-scan-capture="true"
          className="st-qty-input"
          value={quantityText}
          onChange={(e) => setQuantityText(sanitizeQuantityText(e.target.value))}
          onKeyDown={handleKeyDown}
          onFocus={(e) => e.target.select()}
          aria-invalid={!valid}
        />
        <button
          type="button"
          className="st-qty-step"
          onClick={() => step(1)}
          aria-label="Increase quantity by 1"
        >
          <Plus size={24} aria-hidden="true" />
        </button>
      </div>
      <p className="st-qty-hint">Scan the same barcode again to add 1 more.</p>

      {remaining > 0 && quantity !== remaining && (
        <button
          type="button"
          className="st-btn st-btn-secondary st-btn-block"
          onClick={() => setQuantityText(String(remaining))}
        >
          Add remaining ({remaining})
        </button>
      )}

      <p className="st-qty-total" aria-live="polite">
        New total: <strong>{newTotal}</strong> of {expected}
        {newTotal > expected && <span className="st-text-warning"> (over by {newTotal - expected})</span>}
        {!valid && <span className="st-text-danger"> Enter a quantity of 1 or more</span>}
      </p>

      {confirmingLarge && (
        <div className="st-callout st-callout-warning" role="alert">
          <AlertTriangle size={20} aria-hidden="true" />
          <p>
            {newTotal} is much more than the {expected} expected. Check the quantity, then tap
            <strong> Yes, add {quantity}</strong> to confirm.
          </p>
        </div>
      )}
    </Modal>
  );
};

export default QuantityPopup;
